import { memo, useCallback, useMemo } from "react";
import type { IDDItem } from "../../components/common/DropdownMenu";
import Input from "../../components/common/Input";
import DropdownMenu from "../../components/common/DropdownMenu";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import { Trans, useTranslation } from "react-i18next";
import { unitConverter } from "../../components/MeasurementUnits/UnitConverter";
import { setInputValue, updateInputByUnit } from "../../store/slices/gasRateCriticalCalculatorSlice";


interface ToolInputRowProps {
	inputId: string;
}

export const InputRow: React.FC<ToolInputRowProps> = memo(({
	inputId,
}) => {
	const input = useAppSelector(state => {
		return state.gasRateCriticalCalculator.inputs.find(i => i.id === inputId);
	});

	const { i18n } = useTranslation();
	const showHints = useAppSelector(state => state.settings.showHints);

	if (!input) {
		return null;
	}

	const tooltipContent = showHints ? (
		<Trans
			ns={'grc'}
			key={i18n.language}
			i18nKey={`input_titles.${input.id}`}
			components={{
				sub: <sub />,
				sup: <sup />,
			}}
		/>
	) : undefined;

	const labelContent = (
		<Trans
			ns={"grc"}
			key={i18n.language}
			i18nKey={`inputs.${input.id}`}
			components={{
				sub: <sub />,
				sup: <sup />,
			}}
		/>
	);

	const onDDSelectItem = (selectedItemId: string) => {
		dispatch(
			updateInputByUnit({ inputId: input.id, selectedUnitId: selectedItemId })
		);
	}

	const value: number | null = useMemo(() => {
		if (input.currentValue == null)
			return null;

		if (input.currentUnitId != null && input.currentUnitId == input.userUnitId)
			return input.userValue;

		return parseFloat(input.currentValue.toFixed(4));
	}, [input]);

	const dispatch = useAppDispatch();
	const onInputChange = useCallback(
		(inputName: string, value: number | null) => {
			dispatch(
				setInputValue({ inputName: inputName, value: value })
			);
		},
		[dispatch,]
	);

	const dropdownItems = useMemo(() => {
		if (!input.unitCategoryId) {
			return [];
		}
		const units = unitConverter.getUnitsByCategory(input.unitCategoryId);
		return units.map(unit => ({
			id: unit.id,
			translationKey: `measurement_units.${unit.category}.${unit.id}`
		} as IDDItem));
	}, [input.unitCategoryId]);
	return <div className="grc__input-group">
		<label className="grc__input-label" htmlFor={inputId}>
			{labelContent}:
		</label>
		<Input
			value={value}
			onChange={onInputChange}
			name={input.queryName}
			id={input.id}
			tooltip={tooltipContent}
			disabled={false}
			error={input.error}
			errorParams={input.errorParams}
		/>
		{input.unitCategoryId && input.currentUnitId && (
			<DropdownMenu
				inputId={input.id}
				items={dropdownItems}
				selectedItemId={input.currentUnitId}
				onSelect={onDDSelectItem}
				disabled={false}
			/>
		)}
	</div>;
});

